import { put, call } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import {
  LOCAL_STORAGE_VARS,
  API_URL,
  API_PATH_AUTH_SIGNUP,
  API_PATH_AUTH_SIGNIN,
  API_PATH_AUTH_SIGNIN_WITH_TOKEN,
  API_PATH_AUTH_GOOGLE,
  API_PATH_AUTH_PASSWORD_UPDATE,
  API_PATH_AUTH_PASSWORD_FORGET,
  API_PATH_AUTH_PASSWORD_SET,
  API_PATH_AUTH_LOGOUT,
  API_PATH_USERS,
  API_PATH_USERS_LANGUAGE,
  API_PATH_USERS_DISABLE_TASKS_DONE,
  API_PATH_USERS_DISABLE_ADVICE_SHOW,
  API_PATH_USERS_ADVICE_SHOWED_AT,
  API_PATH_USERS_SHOW_GLOBAL_TASKS_AS_LIST,
  API_PATH_USERS_REGISTER_DEVICE_TOKEN,
  API_PATH_USERS_APP_VERSION,
  API_PATH_APP_VERSION,
} from '../../constants/server';
import { DEFAULT_LANGUAGE, ERRORS, TEXTS } from '../../constants/languages';
import { SIGN_IN } from '../../constants/routes';
import { toastOptionsInfo, toastOptionsError } from '../../styles/toast';

import * as actions from '../actions';
import * as localStorageService from '../../services/localStorage';
import { fetchRequest } from '../../server';

import AuthUser from '../../models/users/AuthUser';
import LocalStorageUser from '../../models/users/LocalStorageUser';

const language = localStorageService.getLanguage();

export function* createUserSaga(action) {
  try {
    yield put(actions.createUserStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_AUTH_SIGNUP}`,
      'POST',
      JSON.stringify({
        fullName: action.fullName,
        email: action.email,
        password: action.password,
      }),
    );

    yield put(actions.createUserSuccess());

    toast(TEXTS[DEFAULT_LANGUAGE].VISIT_EMAIL, toastOptionsInfo);
  } catch (error) {
    toast(ERRORS[language][error.code] || error.message, toastOptionsError);
    yield put(actions.createUserFail(error));
  }
}

export function* signInWithEmailSaga(action) {
  try {
    yield put(actions.signInWithEmailStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_AUTH_SIGNIN}`,
      'POST',
      JSON.stringify({
        email: action.email,
        password: action.password,
      }),
    );
    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      throw new Error('No user');
    }

    const authUser = new AuthUser().getAuthUser(dbUser);
    localStorageService.setUser(
      new LocalStorageUser({
        uid: authUser.uid,
        language: authUser.language,
        isDarkMode: localStorageService.getDarkMode(),
      }),
    );

    yield put(actions.signInWithEmailSuccess(authUser));

    window.location.reload();
  } catch (error) {
    toast(ERRORS[language][error.code] || error.message, toastOptionsError);
    yield put(actions.signInWithEmailFail(error));
  }
}

export function* signInWithTokenSaga(action) {
  try {
    yield put(actions.signInWithTokenStart());

    yield call(fetchRequest, `${API_URL}${API_PATH_AUTH_SIGNIN_WITH_TOKEN}`, 'POST', JSON.stringify({ token: action.payload.token }));
    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      throw new Error('No user');
    }

    const authUser = new AuthUser().getAuthUser(dbUser);
    localStorageService.setUser(
      new LocalStorageUser({
        uid: authUser.uid,
        language: authUser.language,
        isDarkMode: localStorageService.getDarkMode(),
      }),
    );

    yield put(actions.signInWithTokenSuccess(authUser));

    window.location.reload();
  } catch (error) {
    toast(ERRORS[language][error.code] || error.message, toastOptionsError);
    yield put(actions.signInWithTokenFail(error));
  }
}

export function* signInWithGoogleSaga() {
  try {
    yield put(actions.signInWithGoogleStart());

    window.location.replace(`${API_URL}${API_PATH_AUTH_GOOGLE}`);

    yield put(actions.signInWithGoogleSuccess());
  } catch (error) {
    toast(ERRORS[language][error.code] || error.message, toastOptionsError);
    yield put(actions.signInWithGoogleFail(error));
  }
}

// export function* signInWithFacebookSaga() {
//   try {
//     yield put(actions.signInWithFacebookStart());
//     const appVersion = yield call([FirebaseApp, 'onceDBOne'], CONST_FIREBASE.APP_VERSION);
//     yield call([FirebaseApp.auth, 'signInWithRedirect'], FirebaseApp.facebookProvider);
//
//     const authUser = yield call([FirebaseApp, 'authStateChanged']);
//
//     const dbUser = new AuthUser({
//       uid: FirebaseApp.auth.currentUser.uid,
//       email: FirebaseApp.auth.currentUser.email,
//       fullName: FirebaseApp.auth.currentUser.displayName,
//       emailVerified: true,
//       isProviderPassword: authUser.isProviderPassword,
//       language: authUser.language || DEFAULT_LANGUAGE,
//       disableTasksDone: authUser.disableTasksDone || false,
//       showGlobalTasksAsList: authUser.showGlobalTasksAsList || false,
//       appVersion,
//     });
//
//     // user check
//     if (!FirebaseApp.auth.currentUser || !FirebaseApp.auth.currentUser.uid) {
//       throw new Error('No user');
//     }
//
//     yield call([FirebaseApp, 'updateDB'], `${CONST_FIREBASE.COLLECTION_USERS}/${FirebaseApp.auth.currentUser.uid}`, dbUser);
//     localStorageService.setUser(new LocalStorageUser({ uid: dbUser.uid, language: dbUser.language }));
//     yield put(actions.signInWithFacebookSuccess(dbUser));
//   } catch (error) {
//     toast(error.message, toastOptionsError);
//     yield put(actions.signInWithFacebookFail(error));
//   }
// }

export function* passwordResetSaga(action) {
  try {
    yield put(actions.passwordResetStart());

    yield call(fetchRequest, `${API_URL}${API_PATH_AUTH_PASSWORD_FORGET}`, 'PATCH', JSON.stringify({ email: action.email }));

    yield put(actions.passwordResetSuccess());
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.passwordResetFail(error));
  }
}

export function* updatePasswordSaga(action) {
  try {
    yield put(actions.updatePasswordStart());

    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      localStorageService.removeUser();
      yield put(actions.authStateChangedSuccess(null));
      return;
    }

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_AUTH_PASSWORD_UPDATE}`,
      'PATCH',
      JSON.stringify({
        email: dbUser.email,
        passwordOld: action.payload.passwordOld,
        passwordNew: action.payload.password,
      }),
    );

    yield put(actions.updatePasswordSuccess());
    toast('Password changed', toastOptionsInfo);
  } catch (error) {
    toast(ERRORS[language][error.code], toastOptionsError);
    yield put(actions.updatePasswordFail(error));
  }
}

export function* setPasswordSaga(action) {
  try {
    yield put(actions.setPasswordStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_AUTH_PASSWORD_SET}`,
      'PATCH',
      JSON.stringify({
        passwordOne: action.payload.passwordOne,
        passwordTwo: action.payload.passwordTwo,
        token: action.payload.token,
      }),
    );

    yield put(actions.setPasswordSuccess());
    toast('Password set', toastOptionsInfo);

    document.location.href = SIGN_IN;
  } catch (error) {
    toast(ERRORS[language][error.code], toastOptionsError);
    yield put(actions.setPasswordFail(error));
  }
}

export function* authStateChangedSaga() {
  try {
    yield put(actions.authStateChangedStart());

    // get user
    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      localStorageService.removeUser();
      yield put(actions.authStateChangedSuccess(null));
      return;
    }

    // get version
    const resultAppVersion = yield call(fetchRequest, `${API_URL}${API_PATH_APP_VERSION}`, 'GET');
    const appVersion = resultAppVersion.data || null;

    const authUser = new AuthUser().getAuthUser(dbUser);

    const userAppVersion = dbUser && dbUser.settings && dbUser.settings.appVersion ? dbUser.settings.appVersion : null;
    const needToReloadAppVersion = appVersion && appVersion !== userAppVersion;
    if (needToReloadAppVersion) {
      yield call(fetchRequest, `${API_URL}${API_PATH_USERS_APP_VERSION}`, 'PATCH', JSON.stringify({ appVersion }));
    }

    const user = localStorageService.getUser();
    const needToReloadUser = LOCAL_STORAGE_VARS.find((v) => {
      return !user || user[v] !== authUser[v];
    });

    if (needToReloadUser) {
      localStorageService.setUser(
        new LocalStorageUser({
          uid: authUser.uid,
          language: authUser.language,
          isDarkMode: localStorageService.getDarkMode(),
        }),
      );
      window.location.reload();
    }

    if (needToReloadAppVersion) {
      window.location.reload();
    }

    yield put(actions.authStateChangedSuccess(authUser));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.authStateChangedFail(error));
  }
}

export function* logoutSaga() {
  try {
    yield put(actions.logoutStart());

    yield call(fetchRequest, `${API_URL}${API_PATH_AUTH_LOGOUT}`, 'POST', JSON.stringify({}));
    localStorageService.removeUser();

    yield put(actions.logoutSuccess());

    document.location.href = SIGN_IN;
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.logoutFail(error));
  }
}

export function* setNamesSaga(action) {
  try {
    yield put(actions.setNamesStart());

    const names = {};

    const firstName = action.payload.names && action.payload.names.firstName ? action.payload.names.firstName : null;
    const lastName = action.payload.names && action.payload.names.lastName ? action.payload.names.lastName : null;
    const fullName = action.payload.names && action.payload.names.fullName ? action.payload.names.fullName : null;
    const nickName = action.payload.names && action.payload.names.nickName ? action.payload.names.nickName : null;

    if (firstName) names.firstName = firstName;
    if (lastName) names.lastName = lastName;
    if (fullName) names.fullName = fullName;
    if (nickName) names.nickName = nickName;

    yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'PATCH', JSON.stringify(names));

    yield put(actions.setNamesSuccess(names));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.setNamesFail(error));
  }
}

export function* setLanguageSaga(action) {
  try {
    yield put(actions.setLanguageStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_USERS_LANGUAGE}`,
      'PATCH',
      JSON.stringify({ language: action.payload.language || DEFAULT_LANGUAGE }),
    );

    const localStorageUser = localStorageService.getUser();
    if (localStorageUser) {
      localStorageService.setUser(
        new LocalStorageUser({
          uid: localStorageUser.uid,
          language: action.payload.language || DEFAULT_LANGUAGE,
          isDarkMode: localStorageService.getDarkMode(),
        }),
      );
    }

    yield put(actions.setLanguageSuccess());

    window.location.reload();
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.setLanguageFail(error));
  }
}

export function* setDarkModeSaga() {
  try {
    yield put(actions.setDarkModeStart());

    const localStorageUser = localStorageService.getUser();
    if (localStorageUser) {
      localStorageService.setUser(
        new LocalStorageUser({
          uid: localStorageUser.uid,
          language: localStorageUser.language,
          isDarkMode: !localStorageService.getDarkMode(),
        }),
      );
    }

    yield put(actions.setDarkModeSuccess());

    window.location.reload();
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.setDarkModeFail(error));
  }
}

export function* setDisableTasksDoneSaga(action) {
  try {
    yield put(actions.setDisableTasksDoneStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_USERS_DISABLE_TASKS_DONE}`,
      'PATCH',
      JSON.stringify({ disableTasksDone: action.payload.disableTasksDone || false }),
    );

    yield put(actions.setDisableTasksDoneSuccess(action.payload.disableTasksDone || false));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.setDisableTasksDoneFail(error));
  }
}

export function* setDisableAdviceShowSaga(action) {
  try {
    yield put(actions.setDisableAdviceShowStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_USERS_DISABLE_ADVICE_SHOW}`,
      'PATCH',
      JSON.stringify({ disableAdviceShow: action.payload.disableAdviceShow || false }),
    );

    yield put(actions.setDisableAdviceShowSuccess(action.payload.disableAdviceShow || false));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.setDisableAdviceShowFail(error));
  }
}

export function* setAdviceShowedAtSaga(action) {
  try {
    yield put(actions.setAdviceShowedAtStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_USERS_ADVICE_SHOWED_AT}`,
      'PATCH',
      JSON.stringify({ adviceShowedAt: action.payload.adviceShowedAt || new Date() }),
    );

    yield put(actions.setAdviceShowedAtSuccess(action.payload.adviceShowedAt || new Date()));
  } catch (error) {
    yield put(actions.setAdviceShowedAtFail(error));
  }
}

export function* setShowGlobalTasksAsListSaga(action) {
  try {
    yield put(actions.setShowGlobalTasksAsListStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_USERS_SHOW_GLOBAL_TASKS_AS_LIST}`,
      'PATCH',
      JSON.stringify({ showGlobalTasksAsList: action.payload.showGlobalTasksAsList || false }),
    );

    yield put(actions.setShowGlobalTasksAsListSuccess(action.payload.showGlobalTasksAsList || false));
  } catch (error) {
    toast(error.message, toastOptionsError);
    yield put(actions.setShowGlobalTasksAsListFail(error));
  }
}

export function* registerDeviceToken(action) {
  try {
    yield put(actions.registerDeviceTokenStart());

    yield call(
      fetchRequest,
      `${API_URL}${API_PATH_USERS_REGISTER_DEVICE_TOKEN}`,
      'POST',
      JSON.stringify({
        platform: action.payload.platform,
        deviceId: action.payload.deviceId,
        token: action.payload.token,
      }),
    );

    yield put(actions.registerDeviceTokenSuccess());
  } catch (error) {
    yield put(actions.registerDeviceTokenFail(error));
  }
}

export function* refreshShowGlobalTasksAsListSaga() {
  try {
    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      localStorageService.removeUser();
      yield put(actions.authStateChangedSuccess(null));
      return;
    }

    const authUser = new AuthUser().getAuthUser(dbUser);

    const user = localStorageService.getUser();
    const needToReload = LOCAL_STORAGE_VARS.find((v) => {
      return !user || user[v] !== authUser[v];
    });
    if (needToReload) {
      localStorageService.setUser(
        new LocalStorageUser({
          uid: authUser.uid,
          language: authUser.language,
          isDarkMode: localStorageService.getDarkMode(),
        }),
      );
      window.location.reload();
    }

    yield put(actions.authStateChangedSuccess(authUser));
  } catch (error) {
    yield put(actions.authStateChangedFail(error));
  }
}

export function* refreshDisableTasksDoneSaga() {
  try {
    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      localStorageService.removeUser();
      yield put(actions.authStateChangedSuccess(null));
      return;
    }

    const authUser = new AuthUser().getAuthUser(dbUser);

    const user = localStorageService.getUser();
    const needToReload = LOCAL_STORAGE_VARS.find((v) => {
      return !user || user[v] !== authUser[v];
    });
    if (needToReload) {
      localStorageService.setUser(
        new LocalStorageUser({
          uid: authUser.uid,
          language: authUser.language,
          isDarkMode: localStorageService.getDarkMode(),
        }),
      );
      window.location.reload();
    }

    yield put(actions.authStateChangedSuccess(authUser));
  } catch (error) {
    yield put(actions.authStateChangedFail(error));
  }
}

export function* refreshDisableAdviceShowSaga() {
  try {
    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      localStorageService.removeUser();
      yield put(actions.authStateChangedSuccess(null));
      return;
    }

    const authUser = new AuthUser().getAuthUser(dbUser);

    const user = localStorageService.getUser();
    const needToReload = LOCAL_STORAGE_VARS.find((v) => {
      return !user || user[v] !== authUser[v];
    });
    if (needToReload) {
      localStorageService.setUser(
        new LocalStorageUser({
          uid: authUser.uid,
          language: authUser.language,
          isDarkMode: localStorageService.getDarkMode(),
        }),
      );
      window.location.reload();
    }

    yield put(actions.authStateChangedSuccess(authUser));
  } catch (error) {
    yield put(actions.authStateChangedFail(error));
  }
}

export function* refreshLanguageSaga() {
  try {
    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      localStorageService.removeUser();
      yield put(actions.authStateChangedSuccess(null));
      return;
    }

    const authUser = new AuthUser().getAuthUser(dbUser);

    const user = localStorageService.getUser();
    const needToReload = LOCAL_STORAGE_VARS.find((v) => {
      return !user || user[v] !== authUser[v];
    });
    if (needToReload) {
      localStorageService.setUser(
        new LocalStorageUser({
          uid: authUser.uid,
          language: authUser.language,
          isDarkMode: localStorageService.getDarkMode(),
        }),
      );
      window.location.reload();
    }

    yield put(actions.authStateChangedSuccess(authUser));

    // reload anyway for language refresh
    window.location.reload();
  } catch (error) {
    yield put(actions.authStateChangedFail(error));
  }
}

export function* refreshUserSaga() {
  try {
    const resultUser = yield call(fetchRequest, `${API_URL}${API_PATH_USERS}`, 'GET');
    const dbUser = resultUser.data || null;
    if (!dbUser) {
      localStorageService.removeUser();
      yield put(actions.authStateChangedSuccess(null));
      return;
    }

    const authUser = new AuthUser().getAuthUser(dbUser);

    const user = localStorageService.getUser();
    const needToReload = LOCAL_STORAGE_VARS.find((v) => {
      return !user || user[v] !== authUser[v];
    });
    if (needToReload) {
      localStorageService.setUser(
        new LocalStorageUser({
          uid: authUser.uid,
          language: authUser.language,
          isDarkMode: localStorageService.getDarkMode(),
        }),
      );
      window.location.reload();
    }

    yield put(actions.authStateChangedSuccess(authUser));
  } catch (error) {
    yield put(actions.authStateChangedFail(error));
  }
}
